// Calculations for the Bodyweight trend card (Nutrition > Progress, Summary
// view): 7-day trend, rate of change, off-trend weigh-ins, logging coverage
// and the projection towards the client's bodyweight goal.
//
// Pure and portable like the rest of this folder: no React, no moment, no
// Rill. Weights are always kg (only the display layer converts to lb) and
// dates are YYYYMMDD numbers, the same `utcDate` shape dailyStats already
// carries. Day math goes through a UTC day number so it can't drift across a
// DST change, which a local-time moment diff could.
//
// Input is the RAW dailyStats list (every weigh-in the client ever logged),
// not the rows scoped to a selected nutrition period: the trend, the rate and
// the off-trend flags all need weigh-ins from BEFORE the visible range, so a
// short range doesn't change what the tiles say.
//
// daily-stats has a unique { userId, utcDate } index and `bodyWeight` is a
// single number, so there is at most one weigh-in per day — whatever was
// written last. If a legacy document with a string userId ever yields two
// documents for one date, the last one in the list wins (same as buildDayRows).

import {
  GOAL_DIRECTION_THRESHOLD_KG,
  OFF_TREND_MIN_PRIOR,
  OFF_TREND_PCT,
  ON_PACE_TOLERANCE_KG,
  PROJECTION_MAX_STALE_DAYS,
  RATE_WINDOW_DAYS,
  STALE_WEIGHIN_DAYS,
  TREND_WINDOW_DAYS,
} from "./constants";

const MS_PER_DAY = 86400000;
const DAYS_PER_WEEK = 7;
// Absorbs float noise right at a threshold (e.g. 60.6 - 60 = 0.6000000000000014)
// so "at least X" comparisons don't flip on representation error.
const EPSILON = 1e-9;

// Values derived from measured weights leave this module rounded to 4
// decimals: far below scale resolution, and it strips float noise that would
// tip a later display rounding the wrong way (60.4 - 59.7 over 4 weeks is
// 0.17499999999999893, which a 2-decimal display would show as 0.17, not 0.18).
// Internal comparisons keep the unrounded values.
const roundOut = (value) =>
  value === null ? null : Math.round(value * 10000) / 10000;

const utcToDayNum = (utc) => {
  const year = Math.floor(utc / 10000);
  const month = Math.floor((utc % 10000) / 100);
  const day = utc % 100;
  return Math.round(Date.UTC(year, month - 1, day) / MS_PER_DAY);
};

const dayNumToUtc = (dayNum) => {
  const date = new Date(dayNum * MS_PER_DAY);
  return (
    date.getUTCFullYear() * 10000 +
    (date.getUTCMonth() + 1) * 100 +
    date.getUTCDate()
  );
};

// day number -> weight, for every valid weigh-in up to today. bodyWeight
// sometimes arrives as a numeric string (see buildDayRows), so it is coerced
// first. A weight of 0 or less is not a weigh-in.
const collectWeighIns = (dailyStats, todayDay) => {
  const byDay = new Map();
  for (const stat of dailyStats) {
    const utc = Number(stat?.utcDate);
    const weight = Number(stat?.bodyWeight);
    if (!Number.isInteger(utc) || !Number.isFinite(weight) || weight <= 0) {
      continue;
    }
    const dayNum = utcToDayNum(utc);
    if (dayNum <= todayDay) byDay.set(dayNum, weight);
  }
  return byDay;
};

// Mean of the weigh-ins in the 7 calendar days ending `day`. Days without a
// weigh-in are skipped, not zero-filled. null when the window has none.
const trendAt = (byDay, day) => {
  let sum = 0;
  let count = 0;
  for (let offset = 0; offset < TREND_WINDOW_DAYS; offset++) {
    const weight = byDay.get(day - offset);
    if (weight !== undefined) {
      sum += weight;
      count++;
    }
  }
  return count > 0 ? sum / count : null;
};

// A weigh-in is off-trend when it sits `offTrendPct` or more away from the
// 7-day average as it stood at the PREVIOUS weigh-in. Comparing against an
// average that already includes the weigh-in itself would let a spike pull
// the reference towards itself and hide. Needs OFF_TREND_MIN_PRIOR weigh-ins
// before it (anywhere in the history, not just in the window).
const buildOffTrendFlags = (byDay, sortedDays, offTrendPct) => {
  const flags = new Map();
  for (let i = OFF_TREND_MIN_PRIOR; i < sortedDays.length; i++) {
    const day = sortedDays[i];
    const ref = trendAt(byDay, sortedDays[i - 1]);
    const delta = byDay.get(day) - ref;
    if (Math.abs(delta) + EPSILON >= offTrendPct * ref) {
      flags.set(day, { delta, ref });
    }
  }
  return flags;
};

const resolveDirection = (toGo) => {
  if (toGo > GOAL_DIRECTION_THRESHOLD_KG) return "gain";
  if (toGo < -GOAL_DIRECTION_THRESHOLD_KG) return "lose";
  return "maintain";
};

// onPace: projected weight within tolerance of the goal. Otherwise "ahead"
// when the projection overshoots in the direction the client is heading
// (past the goal weight), "behind" when it falls short. A maintain goal has
// no direction, so any miss is just "offPace" (wording pending product).
const resolvePaceState = (direction, projectedWeight, targetWeight) => {
  if (projectedWeight === null) return null;
  const gap = projectedWeight - targetWeight;
  if (Math.abs(gap) <= ON_PACE_TOLERANCE_KG + EPSILON) return "onPace";
  if (direction === "maintain") return "offPace";
  const overshoots = gap > 0;
  const wantsMore = direction === "gain";
  return overshoots === wantsMore ? "ahead" : "behind";
};

// The goal record is a client-goals document (type bodyWeight): the weight in
// kg lives in details.targetWeight and the date in utcTargetDate. A goal
// without a date, or whose date has passed, counts as no goal — same as the
// card's "no goal set" state.
const resolveGoal = ({
  bodyweightGoal,
  lastDay,
  todayDay,
  currentTrend,
  rateKgPerWeek,
}) => {
  const targetWeight = Number(bodyweightGoal?.details?.targetWeight);
  if (
    bodyweightGoal?.utcTargetDate == null ||
    !Number.isFinite(targetWeight) ||
    targetWeight <= 0
  ) {
    return null;
  }
  const targetUtc = Number(bodyweightGoal.utcTargetDate);
  if (!Number.isInteger(targetUtc)) return null;

  const goalDay = utcToDayNum(targetUtc);
  if (goalDay < todayDay || goalDay <= lastDay) return null;

  const weeksToGoal = (goalDay - lastDay) / DAYS_PER_WEEK;
  const toGo = targetWeight - currentTrend;
  const direction = resolveDirection(toGo);
  const requiredRateKgPerWeek = toGo / weeksToGoal;
  const projectedWeight =
    rateKgPerWeek === null ? null : currentTrend + rateKgPerWeek * weeksToGoal;

  return {
    targetWeight,
    utcTargetDate: targetUtc,
    direction,
    weeksToGoal,
    toGo: roundOut(toGo),
    requiredRateKgPerWeek: roundOut(requiredRateKgPerWeek),
    projectedWeight: roundOut(projectedWeight),
    paceState: resolvePaceState(direction, projectedWeight, targetWeight),
    goalDay,
  };
};

const emptyResult = (fromUtc, todayUtc) => ({
  hasData: false,
  series: [],
  currentTrend: null,
  trendAtStart: null,
  changeOverRange: null,
  rateKgPerWeek: null,
  daysLogged: 0,
  daysInRange: 0,
  coveragePct: null,
  lastWeighIn: null,
  daysSinceLastWeighIn: null,
  isWeighInStale: false,
  isProjectionStale: false,
  latestOffTrend: null,
  goal: null,
  axis: {
    fromUtc: fromUtc ?? todayUtc,
    toUtc: todayUtc,
    todayIdx: 0,
    lastWeighInIdx: null,
    goalIdx: null,
  },
});

// fromUtc: first day of the selected range; null = from the client's first
//   weigh-in ("All").
// todayUtc: caller-supplied (like buildGoalData) so every consumer on the same
//   render agrees on "today" across a midnight rollover.
// bodyweightGoal: the active client-goals record, or null.
// offTrendPct: override of OFF_TREND_PCT (the threshold is still pending
//   product/design confirmation).
//
// Everything except the range-dependent fields (changeOverRange, daysLogged,
// daysInRange, coveragePct, series, latestOffTrend) is independent of fromUtc.
export const buildBodyweightTrend = ({
  dailyStats = [],
  fromUtc = null,
  todayUtc,
  bodyweightGoal = null,
  offTrendPct = OFF_TREND_PCT,
}) => {
  const todayDay = utcToDayNum(todayUtc);
  const byDay = collectWeighIns(dailyStats, todayDay);
  if (byDay.size === 0) return emptyResult(fromUtc, todayUtc);

  const sortedDays = [...byDay.keys()].sort((a, b) => a - b);
  const firstDay = sortedDays[0];
  const lastDay = sortedDays.at(-1);
  const fromDay = fromUtc == null ? firstDay : utcToDayNum(fromUtc);

  const currentTrend = trendAt(byDay, lastDay);
  const trendBefore = trendAt(byDay, lastDay - RATE_WINDOW_DAYS);
  // With a last weigh-in this old the 4-week rate describes a stretch that is
  // long gone, and every projection would start from that old date: no rate, so
  // no projection and no pace verdict downstream either.
  const isProjectionStale = todayDay - lastDay > PROJECTION_MAX_STALE_DAYS;
  const rateKgPerWeek =
    isProjectionStale || trendBefore === null
      ? null
      : (currentTrend - trendBefore) / (RATE_WINDOW_DAYS / DAYS_PER_WEEK);

  const offTrendFlags = buildOffTrendFlags(byDay, sortedDays, offTrendPct);

  // One point per calendar day from the range start to today. The trend only
  // exists up to the last weigh-in: past it the line stops and the card shades
  // the gap, instead of an average of a window that is mostly empty.
  const series = [];
  for (let day = fromDay; day <= todayDay; day++) {
    const flag = offTrendFlags.get(day);
    series.push({
      idx: day - fromDay,
      utcDate: dayNumToUtc(day),
      weight: byDay.get(day) ?? null,
      trend: day <= lastDay ? roundOut(trendAt(byDay, day)) : null,
      offTrend: flag !== undefined,
      offTrendDelta: flag ? roundOut(flag.delta) : null,
      offTrendRef: flag ? roundOut(flag.ref) : null,
    });
  }

  const daysInRange = Math.max(0, todayDay - fromDay + 1);
  const daysLogged = sortedDays.filter(
    (day) => day >= fromDay && day <= todayDay
  ).length;
  const trendAtStart = series.find((pt) => pt.trend !== null)?.trend ?? null;
  const latestFlagged = series.findLast((pt) => pt.offTrend);
  const daysSinceLastWeighIn = todayDay - lastDay;

  const goal = resolveGoal({
    bodyweightGoal,
    lastDay,
    todayDay,
    currentTrend,
    rateKgPerWeek,
  });

  return {
    hasData: true,
    series,
    currentTrend: roundOut(currentTrend),
    trendAtStart,
    changeOverRange:
      trendAtStart === null ? null : roundOut(currentTrend - trendAtStart),
    rateKgPerWeek: roundOut(rateKgPerWeek),
    daysLogged,
    daysInRange,
    coveragePct:
      daysInRange > 0 ? Math.round((daysLogged / daysInRange) * 100) : null,
    lastWeighIn: { utcDate: dayNumToUtc(lastDay), weight: byDay.get(lastDay) },
    daysSinceLastWeighIn,
    isWeighInStale: daysSinceLastWeighIn > STALE_WEIGHIN_DAYS,
    isProjectionStale,
    latestOffTrend: latestFlagged
      ? {
          utcDate: latestFlagged.utcDate,
          weight: latestFlagged.weight,
          delta: latestFlagged.offTrendDelta,
          ref: latestFlagged.offTrendRef,
        }
      : null,
    goal: goal && {
      targetWeight: goal.targetWeight,
      utcTargetDate: goal.utcTargetDate,
      direction: goal.direction,
      weeksToGoal: goal.weeksToGoal,
      toGo: goal.toGo,
      requiredRateKgPerWeek: goal.requiredRateKgPerWeek,
      projectedWeight: goal.projectedWeight,
      paceState: goal.paceState,
    },
    axis: {
      fromUtc: dayNumToUtc(fromDay),
      toUtc: todayUtc,
      todayIdx: todayDay - fromDay,
      lastWeighInIdx: lastDay - fromDay,
      goalIdx: goal ? goal.goalDay - fromDay : null,
    },
  };
};
